// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {getOAuth2ScopeDescription} from '@app/features/app/constants/AppConstants';
import type {OAuth2Authorization} from '@app/features/auth/commands/OAuth2AuthorizationCommands';
import * as OAuth2AuthorizationCommands from '@app/features/auth/commands/OAuth2AuthorizationCommands';
import {TRY_AGAIN_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import styles from '@app/features/user/components/modals/tabs/AuthorizedAppsTab.module.css';
import {getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import type {OAuth2Scope} from '@fluxer/constants/src/OAuth2Constants';
import {getFormattedShortDate} from '@fluxer/date_utils/src/DateFormatting';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {AppWindowIcon, CaretDownIcon, NetworkSlashIcon} from '@phosphor-icons/react';
import clsx from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

const FAILED_TO_LOAD_AUTHORIZED_APPLICATIONS_DESCRIPTOR = msg({
	message: 'Failed to load authorized applications',
	comment: 'Error message in the authorized apps tab. Keep the tone plain and specific.',
});
const DEAUTHORIZE_APPLICATION_DESCRIPTOR = msg({
	message: 'Deauthorize application',
	comment: 'Short label in the authorized apps tab. Keep it concise. Keep the tone plain and specific.',
});
const ARE_YOU_SURE_YOU_WANT_TO_DEAUTHORIZE_THIS_DESCRIPTOR = msg({
	message:
		'Are you sure you want to deauthorize {appName}? This application will no longer have access to your account.',
	comment: 'Label in the authorized apps tab. Keep the tone plain and specific.',
});
const DEAUTHORIZE_DESCRIPTOR = msg({
	message: 'Deauthorize',
	comment: 'Short label in the authorized apps tab. Keep it concise. Keep the tone plain and specific.',
});
const AUTHORIZED_APPS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'These applications have been granted access to your {productName} account.',
	comment: 'Authorized applications tab description. productName is the Fluxer product name.',
});
const formatDate = (dateString: string): string => {
	return getFormattedShortDate(dateString, getCurrentLocale());
};
const AuthorizedAppsTab = observer(function AuthorizedAppsTab() {
	const {i18n} = useLingui();
	const [authorizations, setAuthorizations] = useState<Array<OAuth2Authorization>>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
	const toggleExpanded = useCallback((appId: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(appId)) {
				next.delete(appId);
			} else {
				next.add(appId);
			}
			return next;
		});
	}, []);
	const loadAuthorizations = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await OAuth2AuthorizationCommands.listAuthorizations();
			setAuthorizations(data);
		} catch (_err) {
			setError(i18n._(FAILED_TO_LOAD_AUTHORIZED_APPLICATIONS_DESCRIPTOR));
		} finally {
			setLoading(false);
		}
	}, []);
	useEffect(() => {
		loadAuthorizations();
	}, [loadAuthorizations]);
	const handleDeauthorize = useCallback((authorization: OAuth2Authorization) => {
		const appName = authorization.application.name;
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(DEAUTHORIZE_APPLICATION_DESCRIPTOR)}
					description={i18n._(ARE_YOU_SURE_YOU_WANT_TO_DEAUTHORIZE_THIS_DESCRIPTOR, {appName})}
					primaryText={i18n._(DEAUTHORIZE_DESCRIPTOR)}
					primaryVariant="danger-primary"
					onPrimary={async () => {
						await OAuth2AuthorizationCommands.deauthorize(authorization.application.id);
						setAuthorizations((prev) => prev.filter((a) => a.application.id !== authorization.application.id));
					}}
					data-flx="user.authorized-apps-tab.handle-deauthorize.confirm-modal"
				/>
			)),
		);
	}, []);
	if (loading) {
		return (
			<div className={styles.loadingContainer} data-flx="user.authorized-apps-tab.loading-container">
				<Spinner size="large" data-flx="user.authorized-apps-tab.spinner" />
			</div>
		);
	}
	return (
		<SettingsTabContainer data-flx="user.authorized-apps-tab.settings-tab-container">
			<SettingsTabContent data-flx="user.authorized-apps-tab.settings-tab-content">
				<SettingsSection
					id="authorized-applications"
					title={<Trans>Authorized applications</Trans>}
					description={i18n._(AUTHORIZED_APPS_DESCRIPTION_DESCRIPTOR, {productName: PRODUCT_NAME})}
					data-flx="user.authorized-apps-tab.authorized-applications"
				>
					{error ? (
						<StatusSlate
							Icon={NetworkSlashIcon}
							title={i18n._(FAILED_TO_LOAD_AUTHORIZED_APPLICATIONS_DESCRIPTOR)}
							description={error}
							actions={[
								{
									text: i18n._(TRY_AGAIN_DESCRIPTOR),
									onClick: loadAuthorizations,
									variant: 'primary',
								},
							]}
							data-flx="user.authorized-apps-tab.status-slate"
						/>
					) : authorizations.length === 0 ? (
						<StatusSlate
							Icon={AppWindowIcon}
							title={<Trans>No authorized applications</Trans>}
							description={<Trans>You haven't authorized any applications to access your account.</Trans>}
							fullHeight
							data-flx="user.authorized-apps-tab.status-slate--2"
						/>
					) : (
						<div className={styles.appList} data-flx="user.authorized-apps-tab.app-list">
							{authorizations.map((authorization) => {
								const iconUrl = authorization.application.icon
									? getUserAvatarURL({
											id: authorization.application.id,
											avatar: authorization.application.icon,
										})
									: null;
								const isExpanded = expandedIds.has(authorization.application.id);
								const authorizedOn = formatDate(authorization.authorized_at);
								return (
									<div
										key={authorization.application.id}
										className={styles.appCard}
										data-flx="user.authorized-apps-tab.app-card"
									>
										<FocusRing offset={-2} data-flx="user.authorized-apps-tab.focus-ring">
											<button
												type="button"
												className={styles.headerButton}
												onClick={() => toggleExpanded(authorization.application.id)}
												aria-expanded={isExpanded}
												data-flx="user.authorized-apps-tab.header-button.toggle-expanded"
											>
												<div className={styles.left} data-flx="user.authorized-apps-tab.left">
													<div className={styles.appAvatar} aria-hidden data-flx="user.authorized-apps-tab.app-avatar">
														{iconUrl ? (
															<img
																src={iconUrl}
																alt={authorization.application.name}
																className={styles.appAvatarImage}
																data-flx="user.authorized-apps-tab.app-avatar-image"
															/>
														) : (
															<AppWindowIcon
																className={styles.appAvatarPlaceholder}
																data-flx="user.authorized-apps-tab.app-avatar-placeholder"
															/>
														)}
													</div>
													<div className={styles.textBlock} data-flx="user.authorized-apps-tab.text-block">
														<div className={styles.titleRow} data-flx="user.authorized-apps-tab.title-row">
															<span className={styles.appName} data-flx="user.authorized-apps-tab.app-name">
																{authorization.application.name}
															</span>
														</div>
														<div className={styles.metaRow} data-flx="user.authorized-apps-tab.meta-row">
															<span className={styles.metaText} data-flx="user.authorized-apps-tab.meta-text">
																<Trans>Authorized on {authorizedOn}</Trans>
															</span>
														</div>
													</div>
												</div>
												<CaretDownIcon
													weight="bold"
													className={clsx(styles.chevron, isExpanded && styles.chevronExpanded)}
													data-flx="user.authorized-apps-tab.chevron"
												/>
											</button>
										</FocusRing>
										{isExpanded && (
											<div className={styles.details} data-flx="user.authorized-apps-tab.details">
												<div className={styles.detailsRow} data-flx="user.authorized-apps-tab.details-row">
													<div className={styles.scopeColumn} data-flx="user.authorized-apps-tab.scope-column">
														<div className={styles.sectionLabel} data-flx="user.authorized-apps-tab.section-label">
															<Trans>Permissions granted</Trans>
														</div>
														<div className={styles.scopeList} data-flx="user.authorized-apps-tab.scope-list">
															{authorization.scopes.map((scope) => (
																<div
																	key={scope}
																	className={styles.scopeTag}
																	data-flx="user.authorized-apps-tab.scope-tag"
																>
																	<span className={styles.scopeName} data-flx="user.authorized-apps-tab.scope-name">
																		{scope}
																	</span>
																	<span
																		className={styles.scopeDescription}
																		data-flx="user.authorized-apps-tab.scope-description"
																	>
																		{getOAuth2ScopeDescription(i18n, scope as OAuth2Scope) || scope}
																	</span>
																</div>
															))}
														</div>
													</div>
													<div className={styles.actions} data-flx="user.authorized-apps-tab.actions">
														<Button
															variant="danger-primary"
															small
															onClick={() => handleDeauthorize(authorization)}
															data-flx="user.authorized-apps-tab.button.deauthorize"
														>
															<Trans>Deauthorize</Trans>
														</Button>
													</div>
												</div>
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default AuthorizedAppsTab;
